import React from 'react';
import { AdminSettings } from '../../types';

type ReservationCodesTabProps = {
  settings: AdminSettings;
  onUpdateSettings: (updates: Partial<AdminSettings>) => void;
};

const buildExample = (prefix: string, nextNumber: number, padding: number) => {
  const padded = String(nextNumber).padStart(padding, '0');
  return `${prefix}-${padded}`;
};

export const ReservationCodesTab: React.FC<ReservationCodesTabProps> = ({ settings, onUpdateSettings }) => {
  const codes = settings.reservationCodes || {
    charter: { enabled: true, prefix: 'CH', nextNumber: 1, padding: 5 },
    scheduled: { enabled: true, prefix: 'SC', nextNumber: 1, padding: 5 },
    booking: { enabled: true, prefix: 'BK', nextNumber: 1, padding: 6 },
  };

  const update = (mode: 'charter' | 'scheduled' | 'booking', updates: Partial<typeof codes.charter>) => {
    onUpdateSettings({
      reservationCodes: {
        ...codes,
        [mode]: {
          ...codes[mode],
          ...updates,
        },
      },
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl border-2 border-slate-200/50 shadow-xl p-8 space-y-8 backdrop-blur-sm bg-gradient-to-br from-white via-blue-50/20 to-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">Reservation Codes</h3>
            <p className="text-sm text-slate-600 mt-0.5 font-medium">
              Separate PNR rules for Charter and Scheduled
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {(['charter', 'scheduled', 'booking'] as const).map(mode => (
            <div key={mode} className="rounded-2xl border-2 border-slate-200/50 bg-gradient-to-br from-white to-blue-50/20 p-6 space-y-5 hover:border-blue-300 hover:shadow-lg transition-all shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">{mode}</p>
                  <p className="text-base font-bold text-slate-900">
                    {mode === 'charter' ? 'Charter PNR' : mode === 'scheduled' ? 'Scheduled PNR' : 'Booking ID'}
                  </p>
                </div>
                <button
                  onClick={() => update(mode, { enabled: !codes[mode].enabled })}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-md ${
                    codes[mode].enabled 
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-emerald-500/40' 
                      : 'bg-slate-100 text-slate-600 border-2 border-slate-200'
                  }`}
                >
                  {codes[mode].enabled ? '✓ Enabled' : 'Disabled'}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Prefix</label>
                  <input
                    value={codes[mode].prefix}
                    onChange={e => update(mode, { prefix: e.target.value.toUpperCase().trim() })}
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-sm font-bold border-2 border-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                    placeholder="CH"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Next Number</label>
                  <input
                    type="number"
                    min={1}
                    value={codes[mode].nextNumber}
                    onChange={e => update(mode, { nextNumber: Number(e.target.value) || 1 })}
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-sm font-mono font-bold border-2 border-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Padding</label>
                  <input
                    type="number"
                    min={2}
                    value={codes[mode].padding}
                    onChange={e => update(mode, { padding: Number(e.target.value) || 2 })}
                    className="w-full bg-white rounded-xl px-3 py-2.5 text-sm font-mono font-bold border-2 border-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200 shadow-sm">
                <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Example</p>
                <p className="text-lg font-mono font-bold text-blue-700">
                  {buildExample(codes[mode].prefix, codes[mode].nextNumber, codes[mode].padding)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
